import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import MyNavbar from '../../../components/Customer/Mynavbar/MyNavbar';
import CategoryCircles from '../../../components/Customer/CategoryCircles/CategoryCircles';
import ProductCard from '../../../components/Customer/ProductCard/ProductCard';
import Footer from '../../../components/Customer/Footer/Footer';
import AppContext from '../../../context/AppContext';
import '../../../assests/css/customer/Home/Home.css';

/*
  ==========================================================
  YEH COMPONENT KYUN BANAYA:
  ==========================================================
  Pehle "/products/:categoryname" route hamesha Kidspage
  khol raha tha, chahe URL mein Women/Men/Accessories kuch
  bhi ho — isi liye har category pe "No products found" aa
  raha tha.

  Yeh naya ProductsPage URL ke ":categoryname" param ko khud
  padhta hai aur usi se products load karta hai — kisi
  context state (selectedCategory) ke bharose nahi baithta.
  Isliye direct URL open karne par ya page refresh karne par
  bhi products sahi load hote hain.

  CATEGORY ID KAHAN SE AATI HAI (NO HARDCODING):
  Hardcoded IDs (women=1, men=2...) use NAHI kiye — agar kal
  backend mein IDs badal jayen to sab tut jata. Iski jagah
  hum wahi API call karte hain jo MyNavbar.jsx pehle se
  istemal kar raha hai (getAllCustomerCategories), aur usme
  se URL ke naam se milta-julta category dhoond kar uski
  asal `id` use karte hain. Yani category list hamesha
  backend se live aati hai.
  ==========================================================
*/

// =====================================================
// FIXED DISPLAY / PRIORITY ORDER
// =====================================================
// IMPORTANT: Yeh EXACT wahi order hai jo
// CategoryCircles.jsx mein bhi use hota hai. Dono jagah
// order match hona ZAROORI hai — warna circles kuch aur
// pehle dikhayenge aur yahan "pehli subcategory ke
// products" kuch aur load ho jayenge (jaisa pehle ho raha
// tha: circles "Stitched" ko pehle dikhate thay lekin
// products "Accessories" ke load ho jate thay, kyunki
// backend API raw order mein "Accessories" pehle bhej raha
// tha aur yahan sorting hi nahi ho rahi thi).
// =====================================================
const SUBCATEGORY_ORDER = [
  'stitched',
  'unstitched',
  'western',
  'accessories',
];

const sortSubcategories = (list) => {
  return [...list].sort((a, b) => {
    const aTitle = (a?.title || '').toLowerCase().trim();
    const bTitle = (b?.title || '').toLowerCase().trim();

    let aIndex = SUBCATEGORY_ORDER.indexOf(aTitle);
    let bIndex = SUBCATEGORY_ORDER.indexOf(bTitle);

    if (aIndex === -1) aIndex = SUBCATEGORY_ORDER.length;
    if (bIndex === -1) bIndex = SUBCATEGORY_ORDER.length;

    return aIndex - bIndex;
  });
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const { categoryname } = useParams();

  const { baseUrl, baseUrlForFiles } = useContext(AppContext);

  const [wishlist, setWishlist] = useState([]);
  const [cart, setCart] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [categoryNotFound, setCategoryNotFound] = useState(false);
  // Currently visible subcategory ka title (heading ke liye) —
  // shuru mein pehli active subcategory ka naam aata hai, phir
  // jab user koi CategoryCircles wala circle click kare to
  // wahi naam update ho jata hai.
  const [activeSubTitle, setActiveSubTitle] = useState('');

  /*
    ==========================================================
    STEP 1: URL ke categoryname se, backend se category list
    fetch karke sahi category ID dhoondo (hardcoded nahi).
    ==========================================================
  */
  useEffect(() => {
    let cancelled = false;
    setCategoryId(null);
    setCategoryNotFound(false);

    axios
      .get(`${baseUrl}/customer/category/getAllCustomerCategories`)
      .then((res) => {
        if (cancelled) return;

        const categories = res.data.categories || [];
        const match = categories.find(
          (c) =>
            (c.category_name || '').toLowerCase().trim() ===
            (categoryname || '').toLowerCase().trim()
        );

        if (match) {
          setCategoryId(match.id);
        } else {
          // URL mein aisa naam hai jo backend ki category list
          // mein maujood hi nahi — genuinely "not found" case
          setCategoryNotFound(true);
        }
      })
      .catch((error) => {
        console.error('Category lookup error:', error);
        if (!cancelled) setCategoryNotFound(true);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryname, baseUrl]);

  /*
    ==========================================================
    LOAD PRODUCTS FOR A SPECIFIC SUBCATEGORY
    (CategoryCircles ke circle par click hone par yeh call
    hota hai, aur pehli-subcategory-auto-load ke liye bhi
    isi function ko reuse karte hain)
    ==========================================================
  */
  const loadAllProductsWithCategory = (category_id, subCat_id, subTitle) => {
    const finalCategoryId = category_id || categoryId;
    if (!finalCategoryId || !subCat_id) return;

    if (subTitle) setActiveSubTitle(subTitle);

    setAllProducts([]);
    setLoading(true);

    axios
      .post(`${baseUrl}/customer/products/loadAllProductForCustomer`, {
        category_id: finalCategoryId,
        subcategory_id: subCat_id,
      })
      .then((res) => {
        setAllProducts(res.data.products || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Products loading error:', error);
        setAllProducts([]);
        setLoading(false);
      });
  };

  /*
    ==========================================================
    STEP 2: categoryId mil jaye to iski subcategories fetch
    karo, SORT karo (CategoryCircles jaisa hi fixed order),
    aur SIRF PEHLI (sorted) active subcategory ke products
    load karo — poori category ke SAARE products ek saath
    nahi dikhane. User jab kisi doosre circle (subcategory)
    par click karega, tab wahi products dikhenge
    (loadAllProductsWithCategory upar handle karta hai).

    FIX: Pehle yahan subcategories ko sort kiye bina hi
    ".find()" kar diya jata tha, jo backend ke RAW order
    (jaisa DB se aaya) mein pehla active item utha leta tha
    — is wajah se circles "Stitched" ko pehle dikhate thay
    lekin products "Accessories" ke load ho jate thay. Ab
    sorting CategoryCircles ke sath EXACTLY match karti hai.
    ==========================================================
  */
  useEffect(() => {
    if (!categoryId) {
      setAllProducts([]);
      setActiveSubTitle('');
      return;
    }

    let cancelled = false;
    setLoading(true);

    const formData = new FormData();
    formData.append('mlcategory_id', categoryId);

    axios
      .post(
        `${baseUrl}/admin/subcategory/getAllSubcategories`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      .then((res) => {
        if (cancelled) return;

        const subs = res.data?.data || [];
        const sortedSubs = sortSubcategories(subs);
        const firstActiveSub = sortedSubs.find((s) => s?.status === 'active');

        if (firstActiveSub) {
          loadAllProductsWithCategory(
            categoryId,
            firstActiveSub.id,
            firstActiveSub.title
          );
        } else {
          // Is category ki koi active subcategory hi nahi hai
          setAllProducts([]);
          setActiveSubTitle('');
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('Subcategory lookup error:', error);
        if (!cancelled) {
          setAllProducts([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, baseUrl]);

  /*
    ==========================================================
    FORMAT PRODUCT (type decide karne ke liye - buy/tryon/
    customize button)
    ==========================================================
  */
  const formatProduct = (product) => {
    const categoryNameLower = (categoryname || '').toLowerCase();
    const subTitle = (product.subcategory?.title || '')
      .toLowerCase()
      .replace(/[^a-z]/g, '');

    let type = 'stitched';

    if (categoryNameLower === 'accessories' || subTitle.includes('accessor')) {
      type = 'accessories';
    } else if (subTitle.includes('unstit') || subTitle.includes('unstich')) {
      type = 'unstitched';
    }

    const images = Array.isArray(product.images)
      ? product.images
          .map((img) =>
            typeof img === 'string'
              ? img
              : img?.image_path
              ? `${baseUrlForFiles}/${img.image_path}`
              : null
          )
          .filter(Boolean)
      : [];

    return {
      ...product,
      images,
      price: product.base_price,
      type,
    };
  };

  const toggleWishlist = (id) =>
    setWishlist((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleCart = (id) =>
    setCart((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  return (
    <div>
      <MyNavbar />
      <br />

      <CategoryCircles
        loadAllProductsWithCategory={loadAllProductsWithCategory}
      />

      <section className="home-products-section">
        <div className="home-products-container">
          <h2 className="home-products-title" style={{ textTransform: 'capitalize' }}>
            {activeSubTitle || categoryname}
          </h2>

          {loading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              Loading...
            </div>
          )}

          <div className="home-products-grid">
            {allProducts.map((product) => {
              const formattedProduct = formatProduct(product);

              return (
                <ProductCard
                  key={product.id}
                  product={formattedProduct}
                  type={formattedProduct.type}
                  wishlist={wishlist}
                  toggleWishlist={toggleWishlist}
                  cart={cart}
                  toggleCart={toggleCart}
                  onCardClick={() =>
                    navigate(`/product-detail/${product.id}`)
                  }
                />
              );
            })}
          </div>

          {categoryNotFound && (
            <p style={{ textAlign: 'center', color: '#999' }}>
              "{categoryname}" category not found.
            </p>
          )}

          {!categoryNotFound && !loading && allProducts.length === 0 && (
            <p style={{ textAlign: 'center', color: '#999' }}>
              No products found.
            </p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;